import React, { Component } from 'react';
import {
    EmvioCard as Card,
    EmvioLoadingSpinner as LoadingSpinner
} from '@nexio/emvio-util-ui';
import {
    EmvioButton as Button,
    EmvioValidatedForm as ValidatedForm,
    EmvioValidatedTextArea as ValidatedTextArea,
    EmvioCheckbox as Checkbox
} from '@nexio/emvio-form-components';
import Dropzone from 'react-dropzone';
import { remove, reduce } from 'lodash';
import './DisputeForm.scss';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import PDFToImage from '../../PDFToImage/PDFToImage';
import { toast } from 'react-toastify';

const MAX_FILE_LIMIT = 10;
const MAX_FILE_SIZE_LIMIT = 4718592; // 4.5 MB because 5 MB is too high

export default class _DisputeForm extends Component {
    static propTypes = {
        canUserDispute: PropTypes.bool,
        isLoading: PropTypes.bool,
        isPreviewLoading: PropTypes.bool,
        isDisclaimerChecked: PropTypes.bool,
        onPreviewDispute: PropTypes.func,
        onSubmit: PropTypes.func,
        merchantId: PropTypes.string
    };

    constructor(props) {
        super(props);

        this.handleTextAreaInput = this.handleTextAreaInput.bind(this);
        this.renderTextArea = this.renderTextArea.bind(this);
        this.renderFileList = this.renderFileList.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onOpenFileDialog = this.onOpenFileDialog.bind(this);
        this.onDrop = this.onDrop.bind(this);
        this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
        this.handlePreviewDispute = this.handlePreviewDispute.bind(this);
        this.renderFileSizeLimitStatus = this.renderFileSizeLimitStatus.bind(this);
        this.handlePDFConversionComplete = this.handlePDFConversionComplete.bind(this);
        this.renderFile = this.renderFile.bind(this);
        this.areAnyPDFsLoading = this.areAnyPDFsLoading.bind(this);
        this.getTotalFileSize = this.getTotalFileSize.bind(this);
        this.renderCBDisclaimer = this.renderCBDisclaimer.bind(this);

        this.state = {
            files: [],
            pdfLoading: {},
            pdfs: [],
            textAreaValue: '',
            userHasNotTypedYet: true,
            isDisclaimerChecked: false
        };
    }

    handleTextAreaInput(event) {
        this.setState({ textAreaValue: event.target.value, userHasNotTypedYet: false });
    }

    renderCBDisclaimer() {
        const merchantIdStr = this.props.merchantId.toString();

        let cbMessage;
        if (merchantIdStr.startsWith('9')) {
            cbMessage = 'If you have any questions about a chargeback, please contact the Chargeback Department at 866-637-5467. Please have the chargeback case number ready to provide.';
        } else if (merchantIdStr.startsWith('5') || merchantIdStr.startsWith('8') || merchantIdStr.startsWith('0008')) {
            cbMessage = 'If you have any questions about a chargeback, please contact the Chargeback Department at 800-858-1166 and choose option 1. Please have the chargeback case number ready to provide.';
        } else {
            cbMessage = 'If you have any questions about a chargeback, please contact the Chargeback Department at 866-637-5467. Please have the chargeback case number ready to provide.';
        }

        return `*Once submitted, documents are provided to a third party. ${cbMessage}`;
    }

    renderTextArea() {
        return (
            <ValidatedTextArea
                labelText="Add your dispute comments here..."
                placeholder="Description"
                value={this.state.textAreaValue}
                onChange={this.handleTextAreaInput}
                disabled={!this.props.canUserDispute}
                validations={['required', 'maximumLength800']}
                userHasNotTypedYet={this.state.userHasNotTypedYet}
                name="disputeFormTextArea">
            </ValidatedTextArea>
        );
    }

    removeFileAtIndex(indexToRemove) {
        const files = [...this.state.files];

        remove(files, (val, index) => {
            return index === indexToRemove;
        });

        this.setState({
            files
        });
    }

    renderFile(file, index) {
        window.URL.revokeObjectURL(file.preview);

        const fileNameClassNames = classNames('file-name', {
            'invalid': file.invalid
        });

        return (
            <div className="file-list-item" key={file.name + index}>
                <div className={fileNameClassNames}>{file.name}</div>
                <button type="button" className="delete-item-button" onClick={() => this.removeFileAtIndex(index)}>
                    <span className="icon-close" />
                </button>
            </div>
        );
    }

    renderFileList() {
        const headerText = this.state.files.length === 1 ? '1 file is attached' : `${this.state.files.length} files are attached`;

        const renderedFiles = this.state.files.map((file, index) => (
            this.renderFile(file, index)
        ));

        return (<div className={classNames('file-list', {
            'loading': this.areAnyPDFsLoading()
        })}>
            <div className="file-list-header">{headerText}</div>
            {renderedFiles}
            {this.areAnyPDFsLoading()
                ? <LoadingSpinner isRelative />
                : null
            }
        </div>);
    }

    renderFileLimitStatus() {
        return (this.state.files.length > MAX_FILE_LIMIT ? <div className="file-upload-error">No more than 10 images may be uploaded</div> : null);
    }

    renderFileSizeLimitStatus() {
        return (this.getTotalFileSize() > MAX_FILE_SIZE_LIMIT ? <div className="file-upload-error">Total file size of images must be less than 4.5 MB</div> : null);
    }

    getTotalFileSize() {
        return reduce(this.state.files, (total, file) => {
            total += file.size;

            return total;
        }, 0);
    }

    onDrop(acceptedFiles, rejectedFiles = []) {
        rejectedFiles = rejectedFiles.map((file) => {
            file.invalid = true;

            return file;
        });

        const files = [...this.state.files, ...acceptedFiles, ...rejectedFiles];
        const filesWithoutPdfs = files.filter((file) => file.type !== 'application/pdf');

        const pdfs = files.filter((file) => file.type === 'application/pdf');
        const pdfLoading = { ...this.state.pdfLoading };
        pdfs.forEach((pdf) => {
            pdfLoading[pdf.name] = true;
        });

        this.setState({
            files: filesWithoutPdfs,
            pdfLoading,
            pdfs
        });
    }

    onSubmit(e) {
        e.preventDefault();
        e.stopPropagation();

        this.props.onSubmit(this.state.textAreaValue, this.state.files);
    }

    onOpenFileDialog() {
        this.dropzoneRef.open();
    }

    handlePreviewDispute() {
        this.props.onPreviewDispute(this.state.textAreaValue, this.state.files);
    }

    handleCheckboxChange() {
        this.setState((previousState) => ({
            isDisclaimerChecked: !previousState.isDisclaimerChecked
        }));
    }

    handlePDFConversionComplete(pages, pdfFileName, hasTooManyPages) {
        if (hasTooManyPages) {
            toast('We do not accept PDFs with more than 15 pages', { type: 'error' });
        }

        const pdfs = [...this.state.pdfs];
        remove(pdfs, (pdf) => (
            pdf.name === pdfFileName
        ));

        this.setState({
            files: [...this.state.files, ...pages],
            pdfLoading: {
                ...this.state.pdfLoading,
                [pdfFileName]: false
            },
            pdfs
        });
    }

    areAnyPDFsLoading() {
        return Object.keys(this.state.pdfLoading).some((key) => this.state.pdfLoading[key]);
    }

    render() {
        const areAnyPDFsLoading = this.areAnyPDFsLoading();

        const hasInvalidFiles = this.state.files.some((file) => file.invalid);
        const hasErrors = this.state.files.length > MAX_FILE_LIMIT
            || this.getTotalFileSize() > MAX_FILE_SIZE_LIMIT
            || hasInvalidFiles;
        const fileUploadContainerClassNames = classNames('file-upload-container', {
            'error': hasErrors
        });

        const fileUploadCaptionClassNames = classNames('file-upload-caption', {
            'error': hasInvalidFiles
        });

        const accessDeniedText = 'Insufficient access rights';
        const canUserSubmitOrPreview = !this.props.canUserDispute || areAnyPDFsLoading;
        const isSubmitDisabled = canUserSubmitOrPreview || hasErrors || !this.state.textAreaValue || !this.state.isDisclaimerChecked;

        return (
            <Card HeaderComponent="Dispute Chargeback" className="grow-height">
                <ValidatedForm
                    className="emvio-dispute-form"
                    hasExternalErrors={hasErrors}
                    onSubmit={this.onSubmit}
                >
                    <div className="form-fields">
                        {this.renderCBDisclaimer()}
                    </div>
                    <div className="form-fields">
                        {this.renderTextArea()}
                        <div className="file-upload">
                            <Dropzone
                                accept="image/jpeg, image/png, application/pdf"
                                className={fileUploadContainerClassNames}
                                disableClick
                                disabled={!this.props.canUserDispute}
                                name="files"
                                onDrop={this.onDrop}
                                ref={(node) => {
                                    this.dropzoneRef = node;
                                }}>
                                <div className="file-upload-label">
                                    <div>Drag file here to upload</div>
                                    <div>or</div>
                                </div>
                                <Button className="file-upload-button"
                                    disabled={!this.props.canUserDispute}
                                    type="button"
                                    title={!this.props.canUserDispute ? accessDeniedText : null}
                                    onClick={this.onOpenFileDialog}>
                                    Browse File
                                </Button>
                            </Dropzone>
                            <Button className="file-upload-button mobile"
                                type="button"
                                disabled={!this.props.canUserDispute}
                                onClick={this.onOpenFileDialog}>
                                Browse File
                            </Button>
                            <div className={fileUploadCaptionClassNames}>Accepted file formats: JPEG, PNG, PDF</div>
                            {this.renderFileLimitStatus()}
                            {this.renderFileSizeLimitStatus()}
                            {this.renderFileList()}
                        </div>
                    </div>
                    <div className="disclaimer-row">
                        <span className="checkbox-container">
                            <Checkbox onChange={this.handleCheckboxChange} />
                        </span>
                        <span className="disclaimer-copy">
                            I agree that I am not including sensitive information. Sensitive information includes: full
                            credit card numbers, credit card security codes (CVV/CVC), social security numbers, driver&apos;s
                            license numbers, health records, etc. Your dispute will be rejected and/or deleted if you include sensitive information.
                        </span>
                    </div>
                    <div className="button-row">
                        <Button
                            className="preview-button"
                            type="button"
                            invertColors={true}
                            disabled={isSubmitDisabled}
                            onClick={this.handlePreviewDispute}
                            title={!this.props.canUserDispute ? accessDeniedText : null}
                            isLoading={this.props.isPreviewLoading}>
                            Preview
                        </Button>
                        <Button
                            className="dispute-button"
                            ref={(button) => {
                                this.submitButtonRef = button;
                            }}
                            invertColors={true}
                            disabled={isSubmitDisabled}
                            title={!this.props.canUserDispute ? accessDeniedText : null}
                            isLoading={this.props.isLoading}>
                            Submit
                        </Button>
                    </div>
                    {this.state.pdfs.map((pdf) => <PDFToImage key={pdf.name} onConversionComplete={this.handlePDFConversionComplete} pdf={pdf} />)}
                </ValidatedForm>
            </Card>
        );
    }
}
